// v2.19.22f-1 — Hook auth user (JWT, event-driven).
import { useCallback, useEffect, useState } from "react";

const TOKEN_KEY = "civ_user_token";
const PROFILE_KEY = "civ_user_profile";
const EVENT_NAME = "civ:auth-changed";

function _readProfile() {
  try {
    return JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
  } catch {
    return null;
  }
}

// v3.13.1 — Le rôle peut changer côté serveur APRÈS le login (ex : validation
// commerçant par l'owner). On rafraîchit le profil depuis /api/auth/me à
// l'ouverture (throttle 60s, partagé entre tous les montages du hook).
let _lastProfileRefreshTs = 0;
async function _refreshProfileFromServer() {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) return;
  if (Date.now() - _lastProfileRefreshTs < 60000) return;
  _lastProfileRefreshTs = Date.now();
  try {
    const API = process.env.REACT_APP_BACKEND_URL || "";
    const r = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
    if (!r.ok) return;
    const d = await r.json().catch(() => null);
    if (!d?.ok || !d.user) return;
    const prevRaw = localStorage.getItem(PROFILE_KEY) || "null";
    let prev = {};
    try { prev = JSON.parse(prevRaw) || {}; } catch { prev = {}; }
    const next = { ...prev, ...d.user };
    if (JSON.stringify(next) !== prevRaw) {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(next));
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
    }
  } catch (_) { /* offline : on garde le profil local */ }
}

export function useAuth() {
  const [user, setUser] = useState(_readProfile);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    const onChange = (e) => {
      setUser((e && e.detail) || null);
      setToken(localStorage.getItem(TOKEN_KEY));
    };
    window.addEventListener(EVENT_NAME, onChange);
    _refreshProfileFromServer();
    return () => window.removeEventListener(EVENT_NAME, onChange);
  }, []);

  const setSession = useCallback((nextUser, nextToken, extras = null) => {
    if (nextToken) localStorage.setItem(TOKEN_KEY, nextToken);
    if (nextUser) localStorage.setItem(PROFILE_KEY, JSON.stringify(nextUser));
    // 2026-09 — Isolation panier/halo par compte : localCartStore + presenceChime
    // scoppent leur clé sur civ_user_phone.
    if (nextUser && nextUser.phone) {
      try { localStorage.setItem('civ_user_phone', nextUser.phone); } catch (_) { /* stockage indisponible */ }
      // 2026-10 — Login réel : on sort du mode invité (sinon Mon Compte reste "Mode invité").
      if (!String(nextUser.phone).startsWith('guest')) {
        try { localStorage.removeItem('civ_is_guest'); } catch (_) { /* stockage indisponible */ }
      }
    }
    // 2026-07-08 (owner) — SESSIONS PAR COMPTE : l'identité applicative
    // (profil/adresses/commandes/conversations) devient l'UUID DU COMPTE.
    // Fini l'identité partagée entre comptes sur un même appareil.
    if (nextUser && nextUser.user_uuid) {
      try {
        const prev = localStorage.getItem('jsonfirst_civ_userid');
        if (prev !== nextUser.user_uuid) {
          localStorage.setItem('jsonfirst_civ_userid', nextUser.user_uuid);
          window.dispatchEvent(new CustomEvent('aya:userid-changed', { detail: nextUser.user_uuid }));
        }
      } catch (_) { /* stockage indisponible */ }
    }
    if (extras && typeof extras === 'object') {
      // v2.19.28 — Onboarding hint éphémère (post-login). N'écrase pas s'il existe déjà la même valeur.
      try { sessionStorage.setItem('aya_login_extras', JSON.stringify(extras)); } catch (_) {}
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: nextUser }));
  }, []);

  const logout = useCallback(async () => {
    try {
      const t = localStorage.getItem(TOKEN_KEY);
      const API = process.env.REACT_APP_BACKEND_URL || "";
      if (t) {
        await fetch(`${API}/api/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${t}` },
        }).catch(() => {});
      }
    } catch (_) {
      /* swallow */
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
    try { localStorage.removeItem('civ_is_guest'); } catch (_) { /* noop */ }
    try { localStorage.removeItem('civ_user_phone'); } catch (_) { /* noop */ }
    // v3.17.1 — Logout propre (démo investisseur) : purge conversations,
    // extras de session et paniers locaux — l'app repart sur un écran vierge.
    try {
      localStorage.removeItem('jsonfirst_civ_conversations');
      sessionStorage.removeItem('aya_login_extras');
      const toDrop = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k && k.startsWith('aya_local_cart_')) toDrop.push(k);
      }
      toDrop.forEach((k) => localStorage.removeItem(k));
    } catch (_) { /* stockage indisponible */ }
    // 2026-07-08 — Déconnexion : nouvelle identité ANONYME (le prochain utilisateur
    // de l'appareil n'hérite JAMAIS des données du compte précédent).
    try {
      const fresh = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `anon-${Date.now()}`;
      localStorage.setItem('jsonfirst_civ_userid', fresh);
      window.dispatchEvent(new CustomEvent('aya:userid-changed', { detail: fresh }));
    } catch (_) { /* stockage indisponible */ }
    setUser(null);
    setToken(null);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
  }, []);

  return { user, token, isAuthenticated: !!token, setSession, logout };
}
